/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ClusterApiService } from '../../http';
import { ErrorResponseModel } from '../../../../../core/models';
import { Injectable, OnDestroy } from '@angular/core';
import { LogService, NotifyService, UserService, UtilsService } from '../../../../../core/services';
import { NodeModel, NodeStatusModel } from '../../models';
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ClusterService implements OnDestroy {
    /**
     * Our event emitter to let components know our node array has changed
     */
    public pulseStatusEmitter: Subject<any> = new Subject<any>();

    /**
     * Our event emitter to let components know our node array has changed
     */
    public nodesEmitter: Subject<NodeModel[]> = new Subject<NodeModel[]>();

    /**
     * Let components know our local node has changed
     */
    public localNodeEmitter: Subject<NodeModel> = new Subject<NodeModel>();

    /**
     * Store pulse status.
     * @private
     */
    private _pulseHaStatus: any;

    /**
     * Store nodes
     * @private
     */
    private _nodes: NodeModel[] = [];

    /**
     * Store nodes
     * @private
     */
    private _localNode: NodeModel = <NodeModel>{};

    /**
     * Store the local node appliance ID.
     * @private
     */
    private _localNodeApplianceId: string = '';

    /**
     * Stored pulse status for comparison.
     * @private
     */
    private storedPulseStatus: string;

    /**
     * Stored socket data for comparison.
     * @private
     */
    private storedNodeSocketData: string;

    /**
     * Stored Timestamp of the last socket event.
     * @private
     */
    private timeStamp: number;

    /**
     * Stored intervalId for use in OnDestroy.
     * @private
     */
    private intervalId: any;

    constructor(
        private clusterApiService: ClusterApiService,
        private logService: LogService,
        private notifyService: NotifyService,
        private utilsService: UtilsService,
        private userService: UserService
    ) {
        this.onInitialise();
        this.subscribeToUserService();
    }

    onInitialise(): void {
        this.timeStamp = new Date().getTime();
        this.intervalId = setInterval((): void => {
            if (Math.abs(this.timeStamp - new Date().getTime()) > 15000) {
                this.getDataFromApi();
                this.logService.debug('ClusterService:onInitialise', 'Socket data out of date, making an API call.');
            }
        }, 15000);
    }

    ngOnDestroy(): void {
        this.tearDownService();
    }

    set pulseHaStatus(pulseHa: any) {
        this.timeStamp = new Date().getTime();

        const pulseStatus = { status: pulseHa.status, message: pulseHa.message };
        if (this.storedPulseStatus !== JSON.stringify(pulseStatus)) {
            this.storedPulseStatus = JSON.stringify(pulseStatus);
            this._pulseHaStatus = pulseStatus;
            this.pulseStatusEmitter.next(pulseStatus);
            if (pulseStatus.status !== 'ok' && pulseStatus.message?.length) {
                const timerId = setTimeout((): void => {
                    this.notifyUser(pulseStatus);
                    clearTimeout(timerId);
                });
            }
        }

        // Only update everyone if the data has changed
        if (this.hasDataChanged(pulseHa.nodes)) {
            this.checkNodeLength(pulseHa.nodes);
            this.nodes = pulseHa.nodes || [];
        }
    }

    get pulseHaStatus(): NodeModel[] {
        return this._pulseHaStatus;
    }

    set nodes(nodes: NodeStatusModel[]) {
        if (nodes.length !== this._nodes.length) {
            this.logService.debug('ClusterService:nodes', 'The status length and nodes length don\'t match');
        }

        if (this.nodes.length) {
            this._nodes = this.patchStatusToCluster(nodes);
            this.nodesEmitter.next(this.nodes);
        }
    }

    get nodes(): NodeModel[] {
        return this._nodes;
    }

    set localNodeApplianceId(localNodeId: any) {
        this._localNodeApplianceId = localNodeId;
    }

    get localNodeApplianceId(): any {
        return this._localNodeApplianceId;
    }

    set localNode(localNode: NodeModel) {
        this._localNode = localNode;
        this.localNodeEmitter.next(this._localNode);
    }

    get localNode(): NodeModel {
        return this._localNode;
    }

    /**
     * Update local node.
     * @param property
     * @param value
     */
    public updateLocalNode(property: string, value: any): void {
        if (this.localNode.hasOwnProperty(property)) {
            this.localNode[property] = value;
        }
    }

    /**
     * Set nodes initial value from resolver in the loadbalancer component.
     * @param nodes
     * @param applianceId
     * @param pulseHa
     */
    public initialiseClusterService(nodes: NodeModel[], pulseHa: any, applianceId?: string): void {
        this._nodes = nodes;
        this.localNodeApplianceId = applianceId ? applianceId : this.localNodeApplianceId;
        this.localNode = this.nodes.find((item: NodeModel): boolean => item.id === this.localNodeApplianceId);
        this.pulseHaStatus = pulseHa;
    }

    /**
     * Get cluster health string.
     */
    public getClusterHealth(): string {
        let healthStatus: string;
        const nodesDown = this.nodes.filter(node => node.status === 'unavailable' || node.status === 'suspicious');
        if (this.nodes.length === 1 || this.nodes.length === 2) {
            healthStatus = nodesDown.length ? 'error' : 'ok';
        } else if (this.nodes.length > 2) {
            healthStatus = nodesDown.length ? 'warning' : 'ok';
        }

        return healthStatus;
    }

    /**
     * Check appliance version on the nodes in our cluster
     */
    public hasNodeVersionMismatch(): boolean {
        if (this.nodes?.length === 1) {
            return false;
        }

        const versions: string[] = this.nodes.map((item: NodeModel): string => item.applianceVersion);
        this.logService.debug('ClusterService:hasNodeVersionMismatch', 'Node versions ' + versions);
        if (versions.includes('unknown')) {
            const unknownIndex: number = versions.findIndex((item: string): boolean => item === 'unknown');
            if (unknownIndex !== -1) {
                versions.splice(unknownIndex, 1);
            }
        }

        return versions.length > 1 && !versions.every((item: string): boolean => item === versions[0]);
    }

    /**
     * Check if we have a cluster.
     */
    public isNodeInCluster(): boolean {
        return this.nodes.length > 1;
    }

    /**
     * Add node to the service.
     */
    public addNode(nodeState: any): void {
        if (nodeState.nodes) {
            this._nodes = nodeState.nodes;
        }
    }

    /**
     * Remove node from service.
     * @param nodeId
     */
    public removeNode(nodeId: string): void {
        const deleteIndex: number = this.nodes.findIndex((item: NodeModel): boolean => item.id === nodeId);
        if (deleteIndex !== -1) {
            this.nodes.splice(deleteIndex, 1);
            this.nodesEmitter.next(this.nodes);
        } else {
            this.logService.debug('ClusterService:removeNode', 'Node id not found');
        }
    }

    /**
     * Tear down service.
     * @private
     */
    private tearDownService(): void {
        clearInterval(this.intervalId);
        this.storedNodeSocketData = undefined;
        this.logService.debug('ClusterService:tearDownService', 'Service cleaned up');
    }

    /**
     * Notify user.
     * @param pulseStatus
     * @private
     */
    private notifyUser(pulseStatus: any): void {
        this.notifyService.removeNotificationById('clusterService');
        this.notifyService.warning(pulseStatus.message, { id: 'clusterService', static: true });
    }

    /**
     * Check node and node status lengths.
     * @param nodes
     * @private
     */
    private checkNodeLength(nodes: any[]): void {
        if (this.nodes.length !== nodes.length) {
            this.getDataFromApi();
        } else {
            const statusIds: string[] = nodes.map((item: NodeStatusModel): string => item.nodeId);
            const nodeIds: string[] = this.nodes.map((item: NodeModel): string => item.id);

            if (statusIds.sort().join(',') !== nodeIds.sort().join(',')) {
                this.getDataFromApi();
            }
        }
    }

    /**
     * Patch socket status to nodes variable.
     * @param nodes
     * @private
     */
    private patchStatusToCluster(nodes: NodeStatusModel[]): NodeModel[] {
        const patchedNodes: any[] = [];
        for (const node of this.nodes) {
            const match: NodeStatusModel = nodes.find((item: NodeStatusModel): boolean => item.nodeId === node.id);
            if (match) {
                node.lastReceived = match.lastReceived;
                node.latency = match.latency;
                node.status = match.status;
                node.statusCode = match.statusCode;
            }

            patchedNodes.push(node);
        }

        return patchedNodes;
    }

    /**
     * Check to see of the data has changed.
     * @param nodes
     * @private
     */
    private hasDataChanged(nodes: any): boolean {
        if (nodes) {
            const cleanNodes: any = this.removeTimeStampRelatedData(nodes);
            if (this.storedNodeSocketData !== JSON.stringify(cleanNodes)) {
                this.storedNodeSocketData = JSON.stringify(cleanNodes);

                return true;
            } else {
                return false;
            }
        } else {
            if (this.storedNodeSocketData) {
                this.storedNodeSocketData = undefined;

                return true;
            } else {
                return false;
            }
        }
    }

    /**
     * Remove time stamp related data.
     * @param nodes
     * @private
     */
    private removeTimeStampRelatedData(nodes: any[]): any [] {
        const clonedNodes: any = this.utilsService.duplicateObject(nodes);

        return clonedNodes.map((node: any): any => {
            delete node.lastReceived;

            return node;
        });
    }

    /**
     * Get data from API.
     * @private
     */
    private getDataFromApi(): void {
        this.clusterApiService.getStatus()
            .subscribe({
                next: (nodeState: any): void => {
                    this.initialiseClusterService(nodeState.nodes, nodeState.pulseha);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ClusterService:getDataFromApi', 'Unable to get data from API', error.message);
                }
            });
    }

    /**
     * Subscribe to user service to detect logout.
     * @private
     */
    private subscribeToUserService(): void {
        this.userService.logOutEmitter
            .subscribe({
                next: (state: boolean): void => {
                    if (state) {
                        this.tearDownService();
                    }
                }
            });
    }
}
